import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, XIcon } from 'lucide-react';
import { notifications, Notification } from '../data/content';
import { useIsMobile } from '../hooks/useIsMobile';

// Timing config differs between mobile and desktop per spec.
const CONFIG = {
  desktop: {
    initialDelay: 8000,
    visible: 4000,
    interval: 6000
  },
  mobile: {
    initialDelay: 10000,
    visible: 3500,
    interval: 8000
  }
};
function pickNext(current: number): number {
  if (notifications.length <= 1) return 0;
  let next = current;
  while (next === current) {
    next = Math.floor(Math.random() * notifications.length);
  }
  return next;
}
export function SocialProofPopup() {
  const isMobile = useIsMobile();
  const [active, setActive] = useState<Notification | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const lastIndex = useRef<number>(-1);
  const timers = useRef<number[]>([]);
  const config = isMobile ? CONFIG.mobile : CONFIG.desktop;
  const clearTimers = useCallback(() => {
    timers.current.forEach(t => window.clearTimeout(t));
    timers.current = [];
  }, []);
  const hideNow = useCallback(() => {
    setActive(null);
  }, []);
  useEffect(() => {
    if (dismissed) {
      clearTimers();
      setActive(null);
      return;
    }
    const showNext = () => {
      // Don't show while the user is typing in an input/textarea.
      const el = document.activeElement;
      const typing = el instanceof HTMLElement && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (typing) {
        const retry = window.setTimeout(showNext, 2000);
        timers.current.push(retry);
        return;
      }
      const next = pickNext(lastIndex.current);
      lastIndex.current = next;
      setActive(notifications[next]);
      const hide = window.setTimeout(() => {
        setActive(null);
        const gap = window.setTimeout(showNext, config.interval);
        timers.current.push(gap);
      }, config.visible);
      timers.current.push(hide);
    };
    const start = window.setTimeout(showNext, config.initialDelay);
    timers.current.push(start);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed, isMobile]);
  return <div className="pointer-events-none fixed bottom-4 left-3 z-50 sm:bottom-5 sm:left-5" data-id="element-170" data-mp="1vvhhcvilqi7i">
      <AnimatePresence data-id="element-171" data-mp="20s7y7l28dhtz">
        {active && !dismissed && <motion.div key={active.name + active.time} initial={{
        x: '-120%',
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        opacity: 0,
        x: 0
      }} transition={{
        x: {
          duration: 0.3,
          ease: 'easeOut'
        },
        opacity: {
          duration: 0.3,
          ease: 'easeIn'
        }
      }} whileHover={{
        boxShadow: '0 14px 34px -10px rgba(61,61,61,0.28)'
      }} onClick={hideNow} role="status" aria-live="polite" className="pointer-events-auto relative w-[220px] max-w-[58vw] cursor-pointer rounded-lg border-l-[3px] border-sage bg-white px-3.5 py-2.5 shadow-[0_8px_24px_-8px_rgba(61,61,61,0.2)] sm:w-[280px] sm:max-w-none" data-id="element-172" data-mp="24tjntql3u326">
            <button onClick={e => {
          e.stopPropagation();
          setActive(null);
        }} aria-label="Close notification" className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-slate/30 hover:text-slate/60" data-id="element-173" data-mp="35f435nlvpk6">
              <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-174" data-mp="qu853gj34nln" />
            </button>

            <div className="flex items-center gap-1.5 pr-4" data-id="element-175" data-mp="luvk71brxqlp">
              <CheckCircle2Icon className="h-3.5 w-3.5 shrink-0 text-sage" aria-hidden="true" data-id="element-176" data-mp="lxhrczur643e" />
              <span className="text-[11px] font-bold text-slate" data-id="element-177" data-mp="gyekd9v7pnss">Verified Purchase</span>
            </div>
            <p className="mt-1 text-[11px] leading-snug text-slate" data-id="element-178" data-mp="36jj9y5ymmpdo">
              {active.name} — {active.location}
            </p>
            <p className="mt-0.5 text-[10px] font-medium text-sage" data-id="element-179" data-mp="uasvz2gd2ku7">{active.time}</p>
            <p className="mt-0.5 text-[10px] text-slate/50" data-id="element-180" data-mp="z22jnxicne3g">NutriPro Complete System™</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}